import { format, formatDistance, formatDistanceToNowStrict, isBefore, isToday, isTomorrow } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

/**
 * This module is the only place in the SPA that turns a date or time into text
 * for a user to read (#3140). Everything here formats through date-fns with an
 * explicit pattern, so output does not follow the viewer's browser locale — two
 * officers in one club read the same instant as the same day. `Intl` appears
 * below only where it is doing timezone arithmetic rather than display, and an
 * ESLint `no-restricted-syntax` rule bans `toLocale*` / `Intl.DateTimeFormat`
 * everywhere else under `src/`, so the regime cannot re-fork.
 *
 * When #2170 introduces a club locale, this is the one file it has to change.
 */

interface FormatMeetingDateOptions {
  timeZone?: string;
}

export interface FormatDateOptions {
  /**
   * IANA timezone to render the instant in. Ignored for a bare `YYYY-MM-DD`
   * input, which names a calendar day and has no instant to convert.
   */
  timeZone?: string;
  /**
   * Rendered when the input is absent (`null` / `undefined` / `""`) or is not a
   * real date. Defaults to `""`.
   *
   * One knob covers both cases deliberately. The call sites this module
   * absorbed had inherited five different answers for the same absent input
   * (`""`, `"-"`, `"Never"`, `"N/A"`, the raw ISO string), because each wrote
   * its own guard; which of those a screen wants is a display decision the
   * caller states once, not something a parser should decide.
   */
  fallback?: string;
}

/**
 * Resolve any accepted input to a `Date`, or `null` when there is nothing real
 * to format.
 *
 * A bare `YYYY-MM-DD` string is parsed as a LOCAL calendar day rather than
 * through `new Date(...)`, which reads it as UTC midnight and renders the
 * previous day anywhere west of UTC. Every call site this module replaced had
 * hand-written that guard, or had the bug.
 *
 * Exported as {@link resolveDisplayDate} so a caller that needs the `Date`
 * itself — to compare it, or to do arithmetic before displaying it — does not
 * have to reach for date-fns and re-derive the bare-YMD rule.
 */
function toDisplayDate(input: string | number | Date | null | undefined, timeZone?: string): Date | null {
  if (input === null || input === undefined || input === "") return null;

  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    // Calendar-invalid values (`2024-02-31`) come back null rather than rolling
    // forward, which is what the caller wants to hear about.
    return parseBareYmdToLocalDate(input);
  }

  const date = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(date.getTime())) return null;

  if (timeZone) {
    // An unusable timezone falls back to the viewer's local rendering rather
    // than losing the value entirely. `toZonedTime` signals that two ways
    // depending on the runtime — it throws on some, and returns an Invalid Date
    // on others — so check both, or `format` throws RangeError downstream.
    try {
      const zoned = toZonedTime(date, timeZone);
      if (!Number.isNaN(zoned.getTime())) return zoned;
    } catch {
      // Fall through to local rendering.
    }
  }

  return date;
}

function formatDisplay(
  input: string | number | Date | null | undefined,
  pattern: string,
  options?: FormatDateOptions,
): string {
  const date = toDisplayDate(input, options?.timeZone);
  return date ? format(date, pattern) : (options?.fallback ?? "");
}

export { toDisplayDate as resolveDisplayDate };

/** Date only, e.g. "Jan 5, 2026". */
export function formatDate(input: string | number | Date | null | undefined, options?: FormatDateOptions): string {
  return formatDisplay(input, "MMM d, yyyy", options);
}

/** Date with a short weekday, e.g. "Mon, 5 Jan 2026". */
export function formatDateWithWeekday(
  input: string | number | Date | null | undefined,
  options?: FormatDateOptions,
): string {
  return formatDisplay(input, "EEE, d MMM yyyy", options);
}

/** Day and month only, e.g. "5 Jan" — for lists where the year is implied. */
export function formatDayMonth(input: string | number | Date | null | undefined, options?: FormatDateOptions): string {
  return formatDisplay(input, "d MMM", options);
}

/**
 * Day of the month alone, e.g. "5" — the companion to {@link formatMonthAbbrev}
 * for a calendar tile that renders the two in separate elements.
 */
export function formatDayOfMonth(
  input: string | number | Date | null | undefined,
  options?: FormatDateOptions,
): string {
  return formatDisplay(input, "d", options);
}

/**
 * Year alone, e.g. "2026". Thin, but a bare `getFullYear()` in a template is
 * still a date rendered outside this module, and a non-Gregorian club locale
 * would need it to come through here like every other field (#2170).
 */
export function formatYear(input: string | number | Date | null | undefined, options?: FormatDateOptions): string {
  return formatDisplay(input, "yyyy", options);
}

/**
 * Month abbreviation only, e.g. "Jan" — for calendar-tile chrome that renders
 * the day number in its own element.
 */
export function formatMonthAbbrev(
  input: string | number | Date | null | undefined,
  options?: FormatDateOptions,
): string {
  return formatDisplay(input, "MMM", options);
}

/** Month and year only, e.g. "Jan 2026". */
export function formatMonthYear(input: string | number | Date | null | undefined, options?: FormatDateOptions): string {
  return formatDisplay(input, "MMM yyyy", options);
}

/** Time of day in 12-hour form, e.g. "7:05 PM". `formatMeetingTime` is the 24-hour counterpart. */
export function formatTimeOfDay(input: string | number | Date | null | undefined, options?: FormatDateOptions): string {
  return formatDisplay(input, "h:mm a", options);
}

/**
 * Time of day to the second, e.g. "7:05:32 PM". Only for indicators where the
 * user is watching something land (an autosave), so the minute alone would not
 * tell them whether it just happened.
 */
export function formatTimeOfDayWithSeconds(
  input: string | number | Date | null | undefined,
  options?: FormatDateOptions,
): string {
  return formatDisplay(input, "h:mm:ss a", options);
}

/**
 * Minutes that `timeZone` is offset from UTC at the given instant (DST-aware),
 * e.g. `330` for Asia/Kolkata, `-480` for US Pacific in winter. Returns 0 for a
 * timezone the runtime does not recognise.
 *
 * Timezone arithmetic rather than display, but it lives here because `Intl` does
 * the work and this module owns `Intl`.
 */
export function getTimezoneOffsetMinutes(timeZone: string, at: Date): number {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "longOffset" }).formatToParts(at);
    const offsetPart = parts.find((part) => part.type === "timeZoneName")?.value ?? "GMT";
    // Examples: "GMT", "GMT+05:30", "GMT-08:00"
    const match = offsetPart.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!match) return 0;
    const sign = match[1] === "-" ? -1 : 1;
    const hours = parseInt(match[2], 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;
    return sign * (hours * 60 + minutes);
  } catch {
    return 0;
  }
}

/**
 * Format a timestamp as "MMM d, yyyy h:mm a", optionally in a target timezone.
 */
export function formatDateTime(
  dateInput: string | number | Date | null | undefined,
  options?: FormatDateOptions,
): string {
  return formatDisplay(dateInput, "MMM d, yyyy h:mm a", options);
}

export function formatMeetingDate(dateInput: string | number | Date, options?: FormatMeetingDateOptions): string {
  if (!dateInput) {
    return "";
  }

  const isDateOnlyString = typeof dateInput === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateInput);
  const normalized = toLocalDateYMD(dateInput);
  if (normalized && (!options?.timeZone || isDateOnlyString)) {
    const [yearStr, monthStr, dayStr] = normalized.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);
    const day = Number(dayStr);
    if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      const localDate = new Date(year, month - 1, day);
      return format(localDate, "EEEE, d MMMM yyyy");
    }
  }

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (!Number.isNaN(date.getTime())) {
    if (options?.timeZone) {
      try {
        // Convert to target timezone, then format with date-fns for consistent output
        const zonedDate = toZonedTime(date, options.timeZone);
        return format(zonedDate, "EEEE, d MMMM yyyy");
      } catch {
        // Fallback to local formatting below if timezone conversion fails.
      }
    }
    return format(date, "EEEE, d MMMM yyyy");
  }

  return String(dateInput);
}

/**
 * Format a meeting time in a specific timezone.
 * Returns time in HH:MM format (24-hour). {@link formatTimeOfDay} is the
 * 12-hour counterpart.
 */
export function formatMeetingTime(
  dateInput: string | number | Date | null | undefined,
  options?: FormatDateOptions,
): string {
  return formatDisplay(dateInput, "HH:mm", options);
}

/**
 * Format a meeting time with the short timezone abbreviation appended
 * (e.g. "17:00 GMT", "19:00 BST"). Uses Intl's `timeZoneName: "short"` so
 * the abbreviation is DST-aware. Falls back to plain HH:mm if the timezone
 * is invalid or formatting fails.
 */
export function formatMeetingTimeWithZoneAbbr(
  dateInput: string | number | Date,
  options?: FormatMeetingDateOptions,
): string {
  const baseTime = formatMeetingTime(dateInput, options);
  if (!baseTime) return "";

  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return baseTime;

  const formatOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZoneName: "short",
  };
  if (options?.timeZone) {
    formatOptions.timeZone = options.timeZone;
  }

  try {
    const parts = new Intl.DateTimeFormat("en-GB", formatOptions).formatToParts(date);
    const tzAbbr = parts.find((part) => part.type === "timeZoneName")?.value;
    return tzAbbr ? `${baseTime} ${tzAbbr}` : baseTime;
  } catch {
    return baseTime;
  }
}

/**
 * Format a meeting date in short form (e.g., "Mon, 15 Jan") with timezone support.
 */
export function formatMeetingDateShort(
  dateInput: string | number | Date | null | undefined,
  options?: FormatDateOptions,
): string {
  return formatDisplay(dateInput, "EEE, d MMM", options);
}

/**
 * Parse a bare `YYYY-MM-DD` string into a LOCAL `Date` (midnight local time),
 * avoiding the UTC-rollover trap of `new Date("2026-01-05")` (which parses as
 * UTC midnight and can render the previous calendar day in timezones behind UTC).
 *
 * Returns `null` for anything that isn't a real calendar date — non-matching
 * strings and calendar-invalid values like `2024-02-31` (which `new Date` would
 * silently roll forward to 2 Mar). Callers fall back to the raw input on null.
 */
function parseBareYmdToLocalDate(ymd: string): Date | null {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
    return null;
  }

  const [yearStr, monthStr, dayStr] = ymd.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  const date = new Date(year, month - 1, day);
  // Guard against `new Date(...)` calendar rollover (e.g. 2024-02-31 → 2 Mar):
  // getTime() stays valid, so also confirm the round-trip components match.
  if (
    Number.isNaN(date.getTime()) ||
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

export function formatYmdForDisplay(ymd: string): string {
  const date = parseBareYmdToLocalDate(ymd);
  return date ? format(date, "EEE, MMM d") : ymd;
}

export function formatYmdForShortBritishDisplay(ymd: string): string {
  const date = parseBareYmdToLocalDate(ymd);
  return date ? format(date, "EEE, d MMM yyyy") : ymd;
}

export function formatYmdAsDayMonthYear(ymd: string): string {
  const date = parseBareYmdToLocalDate(ymd);
  return date ? format(date, "d MMM yyyy") : ymd;
}

export function formatYmdForFullDisplay(ymd: string): string {
  const date = parseBareYmdToLocalDate(ymd);
  return date ? format(date, "EEE, MMM d, yyyy") : ymd;
}

/**
 * Rollover-safe "MMM d, yyyy" (e.g. "Jan 5, 2026") for a bare `YYYY-MM-DD`
 * string. Month-first order matches the COT/dashboard views that display
 * training and completion dates. Returns the input unchanged if it isn't a
 * real calendar date.
 */
export function formatYmdAsMonthDayYear(ymd: string): string {
  const date = parseBareYmdToLocalDate(ymd);
  return date ? format(date, "MMM d, yyyy") : ymd;
}

/**
 * Rollover-safe "EEE, d MMMM yyyy" (e.g. "Mon, 5 January 2026") for a bare
 * `YYYY-MM-DD` string — short weekday, full month. Returns the input unchanged
 * if it isn't a real calendar date.
 */
export function formatYmdWithWeekdayLongMonth(ymd: string): string {
  const date = parseBareYmdToLocalDate(ymd);
  return date ? format(date, "EEE, d MMMM yyyy") : ymd;
}

export function getRelativeDate(dateString: string): string {
  const date = new Date(dateString);

  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";

  const distance = formatDistance(date, new Date(), { addSuffix: true });
  return distance;
}

/**
 * Distance from now, strict and unsuffixed, e.g. "3 minutes" — for a countdown
 * that supplies its own framing ("starts in ..."). {@link getRelativeDate} is
 * the suffixed, calendar-aware variant ("in 3 minutes", "Today", "Tomorrow").
 */
export function formatDistanceToNowLabel(input: string | number | Date | null | undefined): string {
  const date = toDisplayDate(input);
  return date ? formatDistanceToNowStrict(date) : "";
}

export function isDateInPast(dateString: string): boolean {
  return isBefore(new Date(dateString), new Date());
}

export function sortDates(dates: string[]): string[] {
  return dates.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
}

export function isBareYmdDateString(value: string | null | undefined): value is string {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * Extract a YYYY-MM-DD local date string from various date inputs without
 * forcing the value through UTC conversions that can shift the calendar date.
 */
export function toLocalDateYMD(value: string | number | Date | null | undefined): string | null {
  if (value === null || value === undefined) return null;

  if (typeof value === "string") {
    const isoPrefixMatch = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoPrefixMatch) {
      return isoPrefixMatch[1];
    }

    const [firstToken] = value.split(/[T\s]/);
    if (firstToken && /^\d{4}-\d{2}-\d{2}$/.test(firstToken)) {
      return firstToken;
    }
  }

  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isNaN(date.getTime())) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return null;
}

/**
 * Extract the calendar day a user should see for a date value.
 * Bare YYYY-MM-DD values are preserved exactly; timestamped values resolve to
 * the local day (or the provided timezone's day).
 */
export function getDisplayedDateYMD(value: string | null | undefined, timeZone?: string): string | null {
  if (!value) return null;
  if (isBareYmdDateString(value)) return value;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  if (timeZone) {
    try {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(date);

      const year = parts.find((part) => part.type === "year")?.value;
      const month = parts.find((part) => part.type === "month")?.value;
      const day = parts.find((part) => part.type === "day")?.value;

      if (year && month && day) {
        return `${year}-${month}-${day}`;
      }
    } catch {
      // Fall through to local parsing
    }
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Calendar-day keys a meeting datetime can legitimately match a stored
 * preferred-speech date against.
 *
 * Two YMD conventions coexist for the SAME meeting and both occur in real data:
 *   - `toLocalDateYMD(iso)` keeps the literal date *prefix* of the ISO string
 *     (ignoring time + timezone). The speech-request date pickers persist
 *     preferred dates this way, so requests created from a meeting view land
 *     under the prefix day.
 *   - `getDisplayedDateYMD(iso, clubTz)` resolves the calendar day in the
 *     club's timezone — the day the meeting header / role-signup section show
 *     users, and the day other call sites can persist.
 *
 * For an evening meeting stored as UTC these diverge across the midnight
 * boundary (e.g. `2026-06-01T23:51Z` → prefix `2026-06-01`, Europe/London day
 * `2026-06-02`). Matching against only one basis silently drops the member's
 * pending card when the request was stored under the other. Returning both
 * keys keeps the card visible regardless of which convention saved it.
 * Identical keys are de-duplicated, so the common (non-boundary) case yields a
 * single key and behaves exactly as a plain equality check.
 */
export function meetingDateMatchKeys(value: string | null | undefined, timeZone?: string): string[] {
  if (!value) return [];
  const keys = new Set<string>();
  const prefix = toLocalDateYMD(value);
  if (prefix) keys.add(prefix);
  const tzDay = getDisplayedDateYMD(value, timeZone);
  if (tzDay) keys.add(tzDay);
  return [...keys];
}

/**
 * Extract a YYYY-MM-DD date string in the provided timezone (falls back to local parsing).
 */
export function getDatePartInZone(value: string | undefined, timeZone?: string): string {
  if (!value) return "";
  return getDisplayedDateYMD(value, timeZone) ?? "";
}

/**
 * Extract an HH:MM time string in the provided timezone (falls back to local parsing).
 */
export function getTimePartInZone(value: string | undefined, timeZone?: string): string {
  if (!value) return "";

  try {
    return formatMeetingTime(value, timeZone ? { timeZone } : undefined);
  } catch {
    // Fall through to basic parsing below
  }

  try {
    const date = new Date(value);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch {
    return "";
  }
}

/**
 * Build an ISO string from date/time parts interpreted in the provided timezone.
 */
export function buildIsoFromParts(datePart: string, timePart: string, timeZone?: string): string | null {
  if (!datePart || !timePart) return null;

  const normalizedTimePart = timePart.length === 5 ? `${timePart}:00` : timePart;
  const dateTimeString = `${datePart}T${normalizedTimePart}`;

  try {
    const parsedDate = timeZone ? fromZonedTime(dateTimeString, timeZone) : new Date(dateTimeString);
    if (isNaN(parsedDate.getTime())) {
      return null;
    }
    return parsedDate.toISOString();
  } catch {
    return null;
  }
}

/**
 * The zone a club's offset-free meeting datetimes are read in: its configured zone, or UTC when that
 * is missing or not a zone, as the backend's `ClubZone.clubZoneId` falls back.
 */
export function meetingZoneOrUtc(timeZone: string | null | undefined): string {
  if (!timeZone) return "UTC";
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
}

/**
 * The instant a stored meeting datetime names, in epoch milliseconds; `NaN` when it does not parse.
 *
 * A value carrying `Z` or an offset keeps that instant. A value without one is wall time in
 * `timeZone`, as the backend's `ClubCalendar.meetingInstant` reads it. The branch is explicit
 * because `fromZonedTime` re-reads an offset-bearing string's fields in the process zone, which
 * moves the instant whenever the viewer is not in the club's zone.
 */
export function meetingInstantMs(value: string, timeZone: string): number {
  if (/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value.trim())) {
    return new Date(value).getTime();
  }
  try {
    return fromZonedTime(value, timeZone).getTime();
  } catch {
    return NaN;
  }
}

/**
 * True when `dateYmd` is on or before `maxYmd`, both compared as bare YYYY-MM-DD
 * strings (no timezone math — caller must ensure both are in the same calendar
 * frame). `null`/empty `maxYmd` means "no window configured" → always true.
 *
 * Used by the speech-request dialog to enforce the club's `memberSpeechRequestAdvanceDays`
 * window strictly: any date past the cutoff is hidden from the picker, and any
 * previously-saved date past the cutoff is filtered out of the edit-mode seed.
 * Companion to {@link filterDatesWithinAdvanceWindow}.
 */
export function isWithinAdvanceWindowYMD(dateYmd: string, maxYmd: string | null | undefined): boolean {
  if (!maxYmd) return true;
  return dateYmd <= maxYmd;
}

/** Returns the subset of `dates` that fall on or before `maxYmd`. */
export function filterDatesWithinAdvanceWindow(dates: readonly string[], maxYmd: string | null | undefined): string[] {
  return dates.filter((d) => isWithinAdvanceWindowYMD(d, maxYmd));
}

/**
 * Returns today's date as YYYY-MM-DD in the given timezone. Falls back to the
 * browser's local date when `timeZone` is undefined/empty. Used to keep the
 * speech-request dialog's cutoff aligned with the backend's `clubToday(club)`
 * (which uses the club's meeting timezone) — without this, a member browsing
 * from a tz ahead of the club's tz near midnight would see the picker disagree
 * with GraphQL validation by one day.
 */
export function todayYmdInTimezone(timeZone?: string | null): string {
  return getDisplayedDateYMD(new Date().toISOString(), timeZone ?? undefined) ?? "";
}

/**
 * Pure calendar arithmetic: returns `ymd + days` as YYYY-MM-DD. Performs the
 * math in UTC ms so it's DST-safe (matches `LocalDate.plusDays` semantics on
 * the backend). Negative `days` works for subtraction.
 */
export function addDaysToYmd(ymd: string, days: number): string {
  const parts = ymd.split("-");
  if (parts.length !== 3) return ymd;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) return ymd;
  const t = new Date(Date.UTC(y, m - 1, d) + days * 86_400_000);
  const yy = t.getUTCFullYear();
  const mm = String(t.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(t.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}
